import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ObservationListItem } from '@models/observation/observation';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ObservationListItemsService } from '@services/observation-list-items.service';
import { Subject, combineLatest } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

export interface ClinicSupplyWithQuantity {
  clinicSupply: ObservationListItem;
  quantity: number;
}

@Component({
  selector: 'app-clinic-supply-selection',
  templateUrl: './clinic-supply-selection.component.html',
  styleUrls: ['./clinic-supply-selection.component.less'],
})
export class ClinicSupplySelectionComponent implements OnInit, OnDestroy {
  unsub: Subject<void>;
  searchFormControl: FormControl;
  supplyTypeGroups = new Map<string, ObservationListItem[]>();
  selectedSupplies = new Map<ObservationListItem, number>();
  loading = false;

  get shouldExpand(): boolean {
    return Boolean(this.searchFormControl.value);
  }

  constructor(private activeModal: NgbActiveModal, private observationListItemsService: ObservationListItemsService) {}

  ngOnInit() {
    this.unsub = new Subject();
    this.loading = true;
    this.searchFormControl = new FormControl('');

    combineLatest([
      this.observationListItemsService.getClinicSupplies(),
      this.searchFormControl.valueChanges.pipe(startWith('')),
    ])
      .pipe(takeUntil(this.unsub))
      .subscribe(([clinicSupplies, searchString]) => {
        this.supplyTypeGroups.clear();
        const searchedSupplies = this.filterSupplies(clinicSupplies, searchString);
        searchedSupplies.forEach((clinicSupply) => {
          const categoryName = clinicSupply.clinicSupplyType?.observationListType?.name || 'Other';
          if (this.supplyTypeGroups.has(categoryName)) {
            this.supplyTypeGroups.get(categoryName).push(clinicSupply);
          } else {
            this.supplyTypeGroups.set(categoryName, [clinicSupply]);
          }
        });
        this.loading = false;
      });
  }

  private filterSupplies(supplies: ObservationListItem[], searchString: string) {
    if (!searchString) return supplies.slice();
    searchString = searchString.toLocaleLowerCase();
    return supplies.filter(
      (clinicSupply) =>
        clinicSupply.name.toLocaleLowerCase().includes(searchString) ||
        clinicSupply.clinicSupplyType?.name.toLocaleLowerCase().includes(searchString)
    );
  }

  supplyIsSelected(supply: ObservationListItem): boolean {
    return this.selectedSupplies.has(supply);
  }

  toggleSupplySelection(supply: ObservationListItem) {
    if (this.selectedSupplies.has(supply)) {
      this.selectedSupplies.delete(supply);
    } else {
      this.selectedSupplies.set(supply, 1);
    }
  }

  unselectSupply(supply: ObservationListItem) {
    this.selectedSupplies.delete(supply);
  }

  updateQuantity(supply: ObservationListItem, quantity: number) {
    if (quantity > 0) {
      this.selectedSupplies.set(supply, quantity);
    }
  }

  getQuantity(supply: ObservationListItem): number {
    return this.selectedSupplies.get(supply) || 1;
  }

  trackBySupply(index: number, entry: any): number {
    return entry.key.id;
  }

  preventFocusLoss(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.stopPropagation();
    }
  }

  closeModal() {
    const result: ClinicSupplyWithQuantity[] = Array.from(this.selectedSupplies.entries()).map(
      ([supply, quantity]) => ({
        clinicSupply: supply,
        quantity: quantity,
      })
    );
    this.activeModal.close(result);
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
